import clsx from 'clsx';
import { memo, useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { useArrowUpdate } from './bracketArrowManager';

import { useWindowSize } from '@hooks/useWindowSize';

import * as classes from './styles.module.scss';

const getRelativeRect = (element: Element, svgContainer: Element) => {
  if (!element || !svgContainer) return null;

  // Get the actual bounding rects
  const elementRect = element.getBoundingClientRect();
  const containerRect = svgContainer.getBoundingClientRect();

  // Calculate position relative to the SVG container
  const relativeRect = {
    top: elementRect.top - containerRect.top,
    right: elementRect.right - containerRect.left,
    bottom: elementRect.bottom - containerRect.top,
    left: elementRect.left - containerRect.left,
    width: elementRect.width,
    height: elementRect.height,
  };

  return relativeRect;
};

interface ArrowProps {
  className?: string;
  targetId: string;
  sourceId: string;
  cornerRadius?: number;
}

const Arrow = ({ className, targetId, sourceId, cornerRadius = 5 }: ArrowProps) => {
  const [pathData, setPathData] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const { isMobile } = useWindowSize();

  // Create stable arrow ID
  const arrowId = useMemo(() => `${sourceId}->${targetId}`, [sourceId, targetId]);

  const updatePath = useCallback(() => {
    const sourceEl = document.getElementById(`bracket--${sourceId}`);
    const targetEl = document.getElementById(`bracket--${targetId}`);
    const svgContainer = svgRef.current;

    if (!sourceEl || !targetEl || !svgContainer) {
      setPathData(null);
      return;
    }

    const sourceRect = getRelativeRect(sourceEl, svgContainer);
    const targetRect = getRelativeRect(targetEl, svgContainer);

    if (!sourceRect || !targetRect) {
      setPathData(null);
      return;
    }

    const startX = sourceRect.right;
    const endX = targetRect.left;
    const startY = sourceRect.top + sourceRect.height / 2;
    const endY = targetRect.top + targetRect.height / 2;

    const isFinalRound =
      targetEl.closest('.pk-d--flex')?.querySelector('h3')?.textContent?.toLowerCase().includes('final') || false;

    if (Math.abs(startY - endY) < 2 && !isFinalRound) {
      const newPathData = `M ${startX} ${startY} H ${endX}`;
      setPathData(newPathData);
      return;
    }

    const horizontalDistance = endX - startX;

    if (isFinalRound && isMobile) {
      const newPathData = `
        M ${startX} ${startY}
        L ${endX} ${endY}
      `
        .trim()
        .replace(/\s+/g, ' ');

      setPathData(newPathData);
      return;
    }

    const cornerX = startX + horizontalDistance * 0.5;
    const adjustedRadius = isMobile ? Math.min(cornerRadius, 3) : cornerRadius;

    const newPathData = `
      M ${startX} ${startY}
      H ${cornerX - adjustedRadius}
      Q ${cornerX} ${startY} ${cornerX} ${startY + (endY > startY ? adjustedRadius : -adjustedRadius)}
      V ${endY - (endY > startY ? adjustedRadius : -adjustedRadius)}
      Q ${cornerX} ${endY} ${cornerX + adjustedRadius} ${endY}
      H ${endX}
    `
      .trim()
      .replace(/\s+/g, ' ');

    setPathData(newPathData);
  }, [cornerRadius, isMobile, sourceId, targetId]);

  // Use the custom arrow update system
  const { register, unregister } = useArrowUpdate(arrowId, updatePath);

  useEffect(() => {
    // Initial update with minimal delay
    const timeoutId = setTimeout(updatePath, 16);

    // Register this arrow for global updates
    register();

    return () => {
      clearTimeout(timeoutId);
      unregister();
    };
  }, [register, unregister, updatePath]);

  return (
    <svg
      ref={svgRef}
      className={clsx(classes.arrow, className, `start--${sourceId}`, `end--${targetId}`)}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        overflow: 'visible',
        zIndex: 1,
      }}
    >
      {pathData !== null && (
        <path
          d={pathData}
          fill='none'
          stroke='var(--_bracket-arrow-color)'
          strokeWidth={2}
          vectorEffect='non-scaling-stroke'
        />
      )}
    </svg>
  );
};

export default memo(Arrow);
